import { useMemo } from 'react';
import { usePagination, useSortBy } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { BodyM } from '@sorare/core/src/atoms/typography';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { Option } from '@sorare/core/src/components/search/Option';
import type {
  AlgoliaCardIndexesName,
  GallerySortName,
  PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import { useI18nSortByItems } from '@sorare/core/src/hooks/search/useI18nSortByItems';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

type Props = {
  sorts: (AlgoliaCardIndexesName | GallerySortName | PrintablePlayerSortName)[];
};
export const useSortWidget = ({ sorts }: Props): FilterWidget => {
  const sortItems = useI18nSortByItems(sorts);

  const { refine, currentRefinement } = useSortBy({ items: sortItems });
  const { refine: setPage } = usePagination();
  const { updateSort } = useMarketplaceLifecycle();

  const sortwidget: FilterWidget = useMemo(
    () => ({
      key: 'sort',
      type: 'sort',
      title: (
        <BodyM bold>
          <FormattedMessage {...filters.sortBy} />
        </BodyM>
      ),
      Component: ({ visible }) => {
        if (!visible) return <FilterSection />;

        return (
          <div className="FilterWidget visible">
            {sortItems.map(sortItem => (
              <div key={sortItem.value}>
                <Option
                  active={currentRefinement === sortItem.value}
                  onClick={() => {
                    setPage(0);
                    refine(sortItem.value);
                    updateSort(sortItem.label as AlgoliaCardIndexesName);
                  }}
                  variant="radio"
                >
                  {sortItem.label}
                </Option>
              </div>
            ))}
          </div>
        );
      },
    }),
    [currentRefinement, refine, setPage, sortItems, updateSort]
  );
  return sortwidget;
};

export const useBackendSortWidget = ({ sorts }: Props): FilterWidget => {
  const sortItems = useI18nSortByItems(sorts);

  const { refine, currentRefinement } = useSortBy({ items: sortItems });
  const { refine: setPage } = usePagination();
  const { updateSort } = useMarketplaceLifecycle();

  const sortwidget: FilterWidget = useMemo(
    () => ({
      key: 'sort',
      type: 'sort',
      title: (
        <BodyM bold>
          <FormattedMessage {...filters.sortBy} />
        </BodyM>
      ),
      Component: ({ visible }) => {
        if (!visible) return <FilterSection />;

        return (
          <div className="FilterWidget visible">
            {sortItems.map(sortItem => (
              <div key={sortItem.value}>
                <Option
                  active={currentRefinement === sortItem.value}
                  onClick={() => {
                    setPage(0);
                    refine(sortItem.value);
                    updateSort(sortItem.label as AlgoliaCardIndexesName);
                  }}
                  variant="radio"
                >
                  {sortItem.label}
                </Option>
              </div>
            ))}
          </div>
        );
      },
    }),
    [currentRefinement, refine, setPage, sortItems, updateSort]
  );
  return sortwidget;
};
